/**
 * Talks to the NevTan SSO directly from the browser — signing in, signing up,
 * and the password-reset flow all happen on the SSO itself, never through
 * this app's own API. Only the resulting SSO token is ever handed to
 * `api.ssoExchange`, which is what turns it into a NevTan AI session.
 *
 * <p>The SSO's own error payloads are not one consistent shape (a thrown
 * exception comes back as an RFC 7807 problem, a handled one as a plain
 * `{status, message}` map), so `request` below reads whichever field is
 * present rather than assuming one.
 */

export class SsoClientError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'SsoClientError'
  }
}

interface SsoEnvelope<T> {
  success: boolean
  message: string
  data: T
}

interface SsoAuthPayload {
  accessToken: string
  tokenType: string
  expiresIn: number
}

async function request<T>(issuer: string, path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')

  const response = await fetch(`${issuer}${path}`, { ...init, headers })
  const body = await response.json().catch(() => null)

  if (!response.ok) {
    const violations = body?.violations && Object.values(body.violations).join(', ')
    const message = body?.detail || body?.message || violations || `Request failed (${response.status})`
    throw new SsoClientError(response.status, message)
  }

  return body as T
}

export const ssoClient = {
  /** Returns the SSO access token; the caller exchanges it for a session. */
  async signin(issuer: string, email: string, password: string): Promise<string> {
    const envelope = await request<SsoEnvelope<SsoAuthPayload>>(issuer, '/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    return envelope.data.accessToken
  },

  signup(
    issuer: string,
    params: { email: string; password: string; firstName: string; lastName?: string; callbackUrl: string },
  ) {
    return request<{ status: string; message: string; email: string }>(issuer, '/auth/signup', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },

  forgotPassword(issuer: string, email: string, callbackUrl: string) {
    return request<{ status: string; message: string }>(issuer, '/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, callbackUrl }),
    })
  },

  resetPassword(issuer: string, token: string, newPassword: string) {
    return request<{ status: string; message: string }>(issuer, '/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
  },

  resendVerification(issuer: string, email: string) {
    return request<{ success: boolean; message: string }>(issuer, '/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },
}
